import styled from "@emotion/styled";
import { LocalDate, LocalDateTime, LocalTime } from "@js-joda/core";
import { AnimatePresence, motion } from "framer-motion";
import React from "react";
import * as Ons from "react-onsenui";
import { API } from "../../API";
import {
  getClockOutRangeStartTime,
  getDutySheetVisitTaskKey,
  isClockOutStartTimeSameAsClockIn,
} from "../../clockInOutUtils";
import { isLoading, Loading } from "../../Loading";
import {
  NursingQuestionVBPAnswer,
  NursingQuestionVBPReportSubmit,
  NursingQuestionVBPWithAnswer,
  PatientDocumentTypeId,
  PlanOfCareItemId,
  VisitInstanceId,
} from "../../messages";
import {
  RequiredDutySheetTask,
  SelectedDutySheetTask,
  VisitInstanceDutySheet,
} from "../../messages/DutySheet";
import { STRINGS } from "../../strings";
import { globalize, translate } from "../../translate";
import { doNothing } from "../../utils/doNothing";
import { HashMap } from "../../utils/HashMap";
import { DocumentsOutlineIcon } from "../../utils/icones";
import { List } from "../../utils/List";
import { shakeElement } from "../../utils/shakeElement";
import { CHEVRON_LEFT_ICON, CHEVRON_RIGHT_ICON } from "../../utils/svgs_new";
import { CicoModalEntity, extractVisitFromCicoModalEntity } from "../../utils/visitUtils";
import ClockInOutModals, { useClockInOutModals } from "../ClockInOutModals";
import DutySheetCompletedModal from "../DutySheetCompletedModal/DutySheetVisitCompletedModal";
import { AppButton } from "../Generic/AppButton";
import { AppCard, AppCardHeader } from "../Generic/AppCard";
import { AppDragableBottomSheet } from "../Generic/AppDragableBottomSheet";
import { AppIcon } from "../Generic/AppIcon";
import { AppAbstractModal } from "../Generic/AppModal/AppAbstractModal";
import { AppOverlay } from "../Generic/AppOverlay";
import Flex from "../Generic/Flex";
import { If } from "../Generic/If";
import MedflytClockInOutFooter from "../MedflytClockInOutFooter";
import MedflytDutySheetActivity from "../MedflytDutySheetActivity";
import { UseContactSupportReturnType } from "../useContactSupport";
import useRefHeight from "../useRefHeight";
import VBPQuestionsSection from "../VBPQuestion/components/VBPQuestionsSection";
import { default as BloodPressureVector } from "/public/assets/blood-pressure.svg?react";
import { default as DutiesVector } from "/public/assets/duties.svg?react";
import { default as VbpVector } from "/public/assets/vbp.svg?react";

interface UseVisitLetsStartBottomSheetProps {
  activeEntity: CicoModalEntity;
  initialDutySheetData: VisitInstanceDutySheet;
  isOpen: boolean;
  promptClockInMapModal: () => void;
  promptClockOutMapModal: () => void;
  postPatientDocAnswer: API["postPatientDocAnswer"];
  postSubmitPatientDocument: API["postSubmitPatientDocument"];
  onHideOrShowVisitBottomSheet: (isOpen: boolean) => void;
  onOpenChangesSavedBanner: () => void;
  onSubmitDutySheet: (visitInstanceId: VisitInstanceId) => void;
}

export interface MedflytVisitLetsStartBottomSheetProps {
  patientInitials: string;
  visitAddress: string;
  isOpen: boolean;
  visitDate: LocalDate;
  patientName: string;
  visitStartTime: LocalTime;
  visitEndTime: LocalTime;
  clockInToleranceMinutes: number;
  clockOutToleranceMinutes: number;
  requiredDutyTasks: List<RequiredDutySheetTask>;
  initialSelectedDuties: SelectedDutySheetTask[];
  mappedSelectedDuties: HashMap<PlanOfCareItemId, { notes: string | null }>;
  canClockIn: boolean;
  canClockOut: boolean;
  canSubmitTimesheet: boolean;
  clockInOutModalsProps: ReturnType<typeof useClockInOutModals>["state"];
  isDutySheetCompleted: boolean;
  hasPlanOfCare: boolean;
  isErrorModalOpen: boolean;
  contactSupport: UseContactSupportReturnType | Loading;
  errorMessage: string | null;
  errorTitle: string | null;
  clockInTime: LocalDateTime | null;
  clockOutTime: LocalDateTime | null;
  isSubmitting: boolean;
  isDutySheetCompletedModalOpen: boolean;
  visitInstanceId: VisitInstanceId;
  vbpItemWithAnswers: NursingQuestionVBPWithAnswer[];
  shouldColorDuties: boolean;
  taskDocumentsTotal?: number;
  taskDocumentsCompleted?: number;
  onChangeVBPQuestion: (vbpQuestion: NursingQuestionVBPWithAnswer) => void;
  onSubmitVBPQuestions: (vbpReport: NursingQuestionVBPReportSubmit) => Promise<void>;
  onAnswerVBPQuestion: (vbpAnswer: NursingQuestionVBPAnswer) => Promise<void>;
  onClickClockIn: () => void;
  onClickClockOut: () => void;
  onDutySheetCompletedModalClose: () => void;
  onRequestOpenErrorModal: (title: string, error: string) => void;
  onRequestCloseErrorModal: () => void;
  onSubmit: () => void;
  onOpenChangesSavedBanner: () => void;
  onSubmitTimesheet: () => void;
  onRequestClose: () => void;
  postPatientDocAnswer: API["postPatientDocAnswer"];
  postSubmitPatientDocument: API["postSubmitPatientDocument"];
}

export function useMedflytVisitLetsStartBottomSheet(props: UseVisitLetsStartBottomSheetProps) {
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [isDutySheetCompletedModalOpen, setIsDutySheetCompletedModalOpen] = React.useState(false);
  const [isErrorModalOpen, setIsErrorModalOpen] = React.useState<boolean>(false);
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
  const [errorTitle, setErrorTitle] = React.useState<string | null>(null);
  const [shouldColorDuties, setColorDuties] = React.useState(false);
  const activeVisit = extractVisitFromCicoModalEntity(props.activeEntity);

  const mappedSelectedDuties = React.useMemo(
    () =>
      HashMap.fromArray(
        props.initialDutySheetData.selectedDuties.map((x: SelectedDutySheetTask) => [
          x.planOfCareItemId,
          { notes: x.notes },
        ])
      ),
    [props.initialDutySheetData.selectedDuties]
  );

  const getApprovedDutiesMap = (duties: SelectedDutySheetTask[]) => {
    return new Map<PlanOfCareItemId, boolean | null>(
      duties.map((x) => {
        return [
          x.planOfCareItemId,
          sessionStorage.getItem(getDutySheetVisitTaskKey(x.planOfCareItemId, activeVisit.id)) !==
            "false",
        ];
      })
    );
  };

  const dutySheetDocument = PatientDocumentTypeId.wrap(-12345); // TODO: Update to new dutysheet

  const visitStartTime = activeVisit.startTimeLocal.toLocalTime();

  const visitEndTime = activeVisit.endTimeLocal.toLocalTime();

  const clockInToleranceMinutes = activeVisit.publicVisitDetails.clockInToleranceMinutes;

  const clockOutToleranceMinutes = activeVisit.publicVisitDetails.clockOutToleranceMinutes;

  const requiredDutyTasks = List.fromArray(props.initialDutySheetData.requiredDutyTasks);

  const handleErrorModalOpen = (title: string, error: string) => {
    setErrorTitle(title);
    setErrorMessage(error);
    setIsErrorModalOpen(true);
  };

  const handleErrorModalClose = () => {
    setIsErrorModalOpen(false);
  };

  const onRequestOpen = () => {
    props.onHideOrShowVisitBottomSheet(true);
  };

  const onRequestClose = () => {
    props.onHideOrShowVisitBottomSheet(false);
  };

  const onClickClockIn = () => {
    props.onHideOrShowVisitBottomSheet(false);
    props.promptClockInMapModal();
  };

  const onClickClockOut = () => {
    props.onHideOrShowVisitBottomSheet(false);
    props.promptClockOutMapModal();
  };

  const handleDutySheetCompletedModalClose = () => {
    setIsDutySheetCompletedModalOpen(false);
    props.onHideOrShowVisitBottomSheet(true);
  };

  function handleSubmit() {
    setColorDuties(false);
    const requiredDuties = requiredDutyTasks
      .filter((task) => task.isRequired)
      .map((task) => task.id);
    const approvedDuties = getApprovedDutiesMap(props.initialDutySheetData.selectedDuties);
    const selectedDuties = Array.from(mappedSelectedDuties.keys());
    const firstUnmarkedRequiredDutyId = requiredDuties.find(
      (id) =>
        !selectedDuties.includes(id) ||
        (approvedDuties.get(id) === false && mappedSelectedDuties.get(id)?.notes === null)
    );
    const isAllMarked = firstUnmarkedRequiredDutyId === undefined;

    if (!isAllMarked) {
      setColorDuties(true);
      scrollToTask(firstUnmarkedRequiredDutyId);
      return;
    }

    setIsSubmitting(true);

    props
      .postSubmitPatientDocument(activeVisit.id, dutySheetDocument, {
        submissionDate: LocalDate.now(),
      })
      .then(() => {
        cleanSessionStorage(selectedDuties);
        setIsSubmitting(false);
        openDutySheetSubmittedModal();
        props.onSubmitDutySheet(activeVisit.id);
      });
  }

  const cleanSessionStorage = (selectedDutiesIds: PlanOfCareItemId[]) => {
    selectedDutiesIds.forEach((id) => {
      sessionStorage.removeItem(getDutySheetVisitTaskKey(id, activeVisit.id));
    });
  };

  const openDutySheetSubmittedModal = () => {
    props.onHideOrShowVisitBottomSheet(false);
    setIsDutySheetCompletedModalOpen(true);
  };

  const scrollToTask = (taskId: PlanOfCareItemId) => {
    const element = document.getElementById(getDutySheetVisitTaskKey(taskId, activeVisit.id));
    if (element !== null) {
      element.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
      element.style.animation = `shake 900ms ease`;
    }
  };

  return {
    bottomSheetProps: {
      clockInTime: activeVisit.clockInTime,
      clockOutTime: activeVisit.clockOutTime,
      clockInToleranceMinutes: clockInToleranceMinutes,
      clockOutToleranceMinutes: clockOutToleranceMinutes,
      canSubmitTimesheet: false,
      visitDate: activeVisit.startTimeLocal.toLocalDate(),
      patientName: activeVisit.confidentialVisitDetails.patientName,
      patientInitials: activeVisit.publicVisitDetails.patientInitials,
      visitAddress: activeVisit.confidentialVisitDetails.patientAddress,
      errorMessage: errorMessage,
      errorTitle: errorTitle,
      isDutySheetCompletedModalOpen: isDutySheetCompletedModalOpen,
      isDutySheetCompleted: activeVisit.isDutySheetCompleted,
      hasPlanOfCare: activeVisit.hasPlanOfCare,
      isErrorModalOpen: isErrorModalOpen,
      isOpen: props.isOpen,
      isSubmitting: isSubmitting,
      requiredDutyTasks: requiredDutyTasks,
      visitStartTime: visitStartTime,
      visitEndTime: visitEndTime,
      visitInstanceId: activeVisit.id,
      shouldColorDuties: shouldColorDuties,
      initialSelectedDuties: props.initialDutySheetData.selectedDuties,
      mappedSelectedDuties: mappedSelectedDuties,
      onClickClockIn: onClickClockIn,
      onClickClockOut: onClickClockOut,
      onDutySheetCompletedModalClose: handleDutySheetCompletedModalClose,
      onOpenChangesSavedBanner: props.onOpenChangesSavedBanner,
      onRequestClose: onRequestClose,
      onRequestOpenVisitLetsStartBottomSheet: onRequestOpen,
      onRequestOpenErrorModal: handleErrorModalOpen,
      onRequestCloseErrorModal: handleErrorModalClose,
      onSubmit: handleSubmit,
      onSubmitTimesheet: () => null,
      postPatientDocAnswer: props.postPatientDocAnswer,
      postSubmitPatientDocument: props.postSubmitPatientDocument,
    },
  };
}

function MedflytVisitLetsStartBottomSheet(props: MedflytVisitLetsStartBottomSheetProps) {
  const formatedStartTime = globalize().formatLocalTime(props.visitStartTime, { time: "short" });
  const formatedEndTime = globalize().formatLocalTime(props.visitEndTime, { time: "short" });
  const [isTasksOpen, setTasksOpen] = React.useState(false);
  const [isReportOpen, setReportOpen] = React.useState(false);
  const [isDraggableClose, setDraggableClose] = React.useState(true);
  const clockOutTimesRef = React.useRef<HTMLDivElement | null>(null);
  const type = props.taskDocumentsTotal !== undefined ? "task" : "visit";

  const { ref, refHeight } = useRefHeight(0, type);

  React.useEffect(() => {
    if (props.isDutySheetCompletedModalOpen) {
      setTasksOpen(false);
      setReportOpen(false);
      setDraggableClose(true);
    }
  }, [props.isDutySheetCompletedModalOpen]);

  const handleOpenReports = () => {
    setReportOpen(!isReportOpen);
    setDraggableClose(!isDraggableClose);
  };

  const handleOpenTasks = () => {
    if (props.requiredDutyTasks.length() > 0) {
      setTasksOpen(!isTasksOpen);
      setDraggableClose(!isDraggableClose);
    }
  };

  const handleClockIn = () => {
    if (props.canClockIn) {
      props.onClickClockIn();
    }
  };

  const handleClose = () => {
    setTasksOpen(false);
    setReportOpen(false);
    setDraggableClose(true);
    props.onRequestClose();
  };

  const handleClockOut = () => {
    if (
      props.canClockOut &&
      (props.isDutySheetCompleted || props.requiredDutyTasks.length() === 0)
    ) {
      props.onClickClockOut();
    } else if (
      clockOutTimesRef.current !== null &&
      !isClockOutStartTimeSameAsClockIn(props.visitStartTime, props.visitEndTime)
    ) {
      shakeElement(clockOutTimesRef, { duration: 900 });
      clockOutTimesRef.current.style.color = "var(--danger-color)";
    } else if (props.canClockOut && !props.isDutySheetCompleted) {
      props.onRequestOpenErrorModal(
        translate(STRINGS.YOU_MUST_SUBMIT_ALL_THE_DUTIES_BEFORE_CLOCKING_OUT),
        translate(STRINGS.PLEASE_MARK_ALL_THE_REQUIRED_DUTIES)
      );
    }
  };

  const requiredCompletedTasksLength = getRequiredCompletedTasksLength(
    props.requiredDutyTasks,
    props.mappedSelectedDuties
  );

  const requiredTasksLength = getRequiredTasksLength(props.requiredDutyTasks);

  return (
    <AppOverlay>
      <MedflytVisitLetsStartBottomSheet.Root
        isOpen={props.isOpen}
        onRequestClose={handleClose}
        closeOnDrag={isDraggableClose}
        height={isDraggableClose ? `${refHeight + 24}px` : "75vh"}
      >
        <AnimatePresence initial={false}>
          {!isReportOpen && !isTasksOpen && (
            <motion.div
              key="all"
              style={{ position: "absolute", width: "100%", height: "100%" }}
              animate={{ x: 0, scale: 1 }}
              initial={{ x: -400 }}
              exit={{ x: -400 }}
              transition={{ type: "spring", stiffness: 500, damping: 50, duration: 0.5 }}
            >
              <MedflytVisitLetsStartBottomSheet.Wrapper>
                <MedflytVisitLetsStartBottomSheet.BottomSheetWrapper direction="column" ref={ref}>
                  <Flex direction="row" alignItems="flex-start">
                    <MedflytVisitLetsStartBottomSheet.CardHeader>
                      <MedflytVisitLetsStartBottomSheet.InitialsBox>
                        {props.patientInitials}
                      </MedflytVisitLetsStartBottomSheet.InitialsBox>
                      <Flex direction="column">
                        <MedflytVisitLetsStartBottomSheet.PatientDataWrapper>
                          <MedflytVisitLetsStartBottomSheet.PatientName>
                            {props.patientName}
                          </MedflytVisitLetsStartBottomSheet.PatientName>
                          <MedflytVisitLetsStartBottomSheet.VisitTitleData>{`| ${formatedStartTime} - ${formatedEndTime}`}</MedflytVisitLetsStartBottomSheet.VisitTitleData>
                        </MedflytVisitLetsStartBottomSheet.PatientDataWrapper>
                        <MedflytVisitLetsStartBottomSheet.VisitAddress>
                          {props.visitAddress}
                        </MedflytVisitLetsStartBottomSheet.VisitAddress>
                      </Flex>
                    </MedflytVisitLetsStartBottomSheet.CardHeader>
                  </Flex>

                  {props.taskDocumentsTotal !== undefined &&
                  props.taskDocumentsCompleted !== undefined ? (
                    <DocumentsToCompleteCard
                      taskDocumentsCompleted={props.taskDocumentsCompleted}
                      taskDocumentsTotal={props.taskDocumentsTotal}
                    />
                  ) : (
                    <>
                      <DutiesCard
                        requiredCompletedTasksLength={requiredCompletedTasksLength}
                        requiredDutyTasks={props.requiredDutyTasks}
                        requiredTasksLength={requiredTasksLength}
                        isDutySheetCompleted={props.isDutySheetCompleted}
                        onClickHeader={handleOpenTasks}
                      />

                      <ReportCard
                        vbpItemWithAnswers={props.vbpItemWithAnswers}
                        onClickHeader={handleOpenReports}
                      />

                      <BloodPressureCard />
                    </>
                  )}

                  <MedflytVisitLetsStartBottomSheet.FooterWrapper>
                    <MedflytVisitLetsStartBottomSheet.ClockInOutFooter>
                      <MedflytClockInOutFooter
                        canClockIn={props.canClockIn}
                        canClockOut={
                          props.canClockOut &&
                          (props.isDutySheetCompleted || props.requiredDutyTasks.length() === 0)
                        }
                        onClockIn={handleClockIn}
                        onClockOut={handleClockOut}
                        clockInDateTime={props.clockInTime}
                        clockOutDateTime={props.clockOutTime}
                      />
                    </MedflytVisitLetsStartBottomSheet.ClockInOutFooter>
                    <Flex direction="row">
                      <MedflytVisitLetsStartBottomSheet.ClockInTimes>
                        {props.canClockIn ? `${formatedStartTime} - ${formatedEndTime}` : null}
                      </MedflytVisitLetsStartBottomSheet.ClockInTimes>

                      <MedflytVisitLetsStartBottomSheet.ClockOutTimes ref={clockOutTimesRef}>
                        {props.canClockOut ||
                        (props.clockOutTime === null && props.visitEndTime.isAfter(LocalTime.now()))
                          ? `${globalize().formatLocalTime(
                              getClockOutRangeStartTime(props.visitStartTime, props.visitEndTime),
                              { time: "short" }
                            )} - ${formatedEndTime}`
                          : null}
                      </MedflytVisitLetsStartBottomSheet.ClockOutTimes>
                    </Flex>
                  </MedflytVisitLetsStartBottomSheet.FooterWrapper>
                </MedflytVisitLetsStartBottomSheet.BottomSheetWrapper>
              </MedflytVisitLetsStartBottomSheet.Wrapper>
            </motion.div>
          )}
        </AnimatePresence>
        <AnimatePresence exitBeforeEnter>
          {isTasksOpen && (
            <motion.div
              key="duties"
              initial={{ x: "100%" }}
              animate={{ x: 0, scale: 1 }}
              exit={{ x: "100%" }}
              transition={{ type: "spring", stiffness: 500, damping: 50, duration: 0.5 }}
            >
              <MedflytVisitLetsStartBottomSheet.ReportsHeader>
                <Flex direction="row" alignItems="center" justifyContent="space-between" xGap={12}>
                  <Flex
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    xGap={12}
                    onClick={handleOpenTasks}
                  >
                    <AppIcon
                      icon={CHEVRON_LEFT_ICON}
                      size={20}
                      style={{ width: 10, marginBottom: 2 }}
                    />
                    <span>{translate(STRINGS.BACK)}</span>
                  </Flex>
                  <Flex
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    xGap={12}
                  >
                    {props.initialSelectedDuties.length > 0 && !props.isDutySheetCompleted && (
                      <MedflytVisitLetsStartBottomSheet.ChangesSaved>
                        {translate(STRINGS.ALL_CHANGES_SAVED)}
                      </MedflytVisitLetsStartBottomSheet.ChangesSaved>
                    )}
                  </Flex>
                </Flex>
              </MedflytVisitLetsStartBottomSheet.ReportsHeader>
              <MedflytVisitLetsStartBottomSheet.Tasks>
                <MedflytDutySheetActivity
                  colorRequiredTasks={props.shouldColorDuties}
                  visitInstanceId={props.visitInstanceId}
                  isDutySheetCompleted={props.isDutySheetCompleted}
                  onRequestOpenErrorModal={props.onRequestOpenErrorModal}
                  requiredDutyTasks={props.requiredDutyTasks}
                  selectedDutyTasks={props.initialSelectedDuties}
                  postPatientDocAnswer={props.postPatientDocAnswer}
                  onOpenChangesSavedBanner={props.onOpenChangesSavedBanner}
                />
              </MedflytVisitLetsStartBottomSheet.Tasks>
              <If condition={!props.isDutySheetCompleted && props.requiredDutyTasks.length() > 0}>
                <MedflytVisitLetsStartBottomSheet.Footer>
                  <If condition={!props.isSubmitting}>
                    <MedflytVisitLetsStartBottomSheet.SubmitReportButton
                      variant="filled"
                      onClick={props.onSubmit}
                    >
                      <MedflytVisitLetsStartBottomSheet.SubmitButton>
                        {`${translate(
                          STRINGS.SUBMIT_DUTIES
                        )} (${requiredCompletedTasksLength}/${requiredTasksLength})`}
                      </MedflytVisitLetsStartBottomSheet.SubmitButton>
                    </MedflytVisitLetsStartBottomSheet.SubmitReportButton>
                  </If>
                  <If condition={props.isSubmitting}>
                    <MedflytVisitLetsStartBottomSheet.Loading>
                      <Ons.ProgressCircular indeterminate={true} />
                    </MedflytVisitLetsStartBottomSheet.Loading>
                  </If>
                </MedflytVisitLetsStartBottomSheet.Footer>
              </If>
            </motion.div>
          )}
        </AnimatePresence>

        <AnimatePresence>
          {!isLoading(props.vbpItemWithAnswers) && isReportOpen && (
            <motion.div
              layout
              key="report"
              initial={{ x: "100%", scale: 1 }}
              animate={{ x: 0, scale: 1 }}
              exit={{ x: "100%", scale: 1 }}
              transition={{ type: "spring", stiffness: 500, damping: 50, duration: 0.5 }}
            >
              <MedflytVisitLetsStartBottomSheet.ReportsHeader>
                <Flex direction="row" alignItems="flex-start" justifyContent="flex-start" xGap={12}>
                  <AppIcon
                    icon={CHEVRON_LEFT_ICON}
                    size={20}
                    style={{ width: 10 }}
                    onClick={handleOpenReports}
                  />
                  <span>{`${translate(STRINGS.PATIENT_REPORT)} (${
                    props.vbpItemWithAnswers.length
                  })`}</span>
                </Flex>
              </MedflytVisitLetsStartBottomSheet.ReportsHeader>
              <MedflytVisitLetsStartBottomSheet.VbpSubTitle>{`${translate(
                STRINGS.PLEASE_ASK_THE_PATIENT
              )}:`}</MedflytVisitLetsStartBottomSheet.VbpSubTitle>

              <VBPQuestionsSection
                showAnsweredIndication={true}
                showTitle={false}
                nursingQuestionsVBPWithAnswers={props.vbpItemWithAnswers}
                onVBPQuestionChange={props.onChangeVBPQuestion}
                onSubmitVBPQuestions={props.onSubmitVBPQuestions}
                onVBPQuestionAnswer={props.onAnswerVBPQuestion}
                onVBPQuestionNotesClick={doNothing}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </MedflytVisitLetsStartBottomSheet.Root>

      <DutySheetCompletedModal
        isOpen={props.isDutySheetCompletedModalOpen}
        onRequestClose={props.onDutySheetCompletedModalClose}
      />

      {isLoading(props.contactSupport) ? null : (
        <AppOverlay>
          <ClockInOutModals
            {...props.clockInOutModalsProps}
            contactSupportProps={props.contactSupport}
          />
        </AppOverlay>
      )}
      <MedflytVisitLetsStartBottomSheet.ErrorModal
        isOpen={props.isErrorModalOpen}
        onRequestClose={props.onRequestCloseErrorModal}
      >
        <Flex direction="column" yGap={24} alignItems="center" justifyContent="center">
          <MedflytVisitLetsStartBottomSheet.ErrorModalHeadline>
            {props.errorTitle}
          </MedflytVisitLetsStartBottomSheet.ErrorModalHeadline>

          <MedflytVisitLetsStartBottomSheet.ErrorModalText>
            {props.errorMessage}
          </MedflytVisitLetsStartBottomSheet.ErrorModalText>
          <AppButton
            variant="outline"
            color="primary"
            size="small"
            onClick={props.onRequestCloseErrorModal}
          >
            {translate(STRINGS.CLOSE)}
          </AppButton>
        </Flex>
      </MedflytVisitLetsStartBottomSheet.ErrorModal>
    </AppOverlay>
  );
}

MedflytVisitLetsStartBottomSheet.Root = styled(AppDragableBottomSheet)``;

MedflytVisitLetsStartBottomSheet.Footer = styled.div`
  position: sticky;
  background-color: var(--scaffold-header-background);
  bottom: 0;
  padding: 12px 12px 20px 12px;
  box-shadow: 0px -4px 5px -6px #333;
  display: flex;
  align-items: center;
  justify-content: center;
`;

MedflytVisitLetsStartBottomSheet.Tasks = styled.div`
  min-height: 57vh;
`;

MedflytVisitLetsStartBottomSheet.SubmitButton = styled.div`
  display: flex;
  flex-direction: row;
  gap: 4px;
  align-items: center;
`;

MedflytVisitLetsStartBottomSheet.ClockInOutFooter = styled.div`
  .ClockInOutFooter {
    box-shadow: none;
    padding: 8px;
  }
`;

MedflytVisitLetsStartBottomSheet.Loading = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
`;

MedflytVisitLetsStartBottomSheet.Card = styled(AppCard)`
  box-shadow: none;
  .AppCardHeader {
    display: flex;
    flex-direction: row;
    align-items: center;
    padding: 20px;
    height: 4vh;
  }
`;

MedflytVisitLetsStartBottomSheet.InitialsBox = styled.span`
  --size: 46px;
  width: var(--size);
  height: var(--size);
  display: flex;
  align-items: center;
  justify-content: center;
  background: #7c3aed;
  border-radius: 12px;
  font-weight: 600;
  font-size: 16px;
  color: white;
`;

MedflytVisitLetsStartBottomSheet.CardHeader = styled(AppCardHeader)`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  padding-bottom: 16px;
  padding-top: 0;
  width: 100%;
`;

MedflytVisitLetsStartBottomSheet.VisitTitleData = styled.span`
  font-weight: 600;
  font-size: 14px;
  line-height: 19px;
  color: #475569;
`;

MedflytVisitLetsStartBottomSheet.VisitAddress = styled.span`
  font-style: normal;
  font-weight: 600;
  font-size: 14px;
  line-height: 19px;
  color: #64748b;
`;

MedflytVisitLetsStartBottomSheet.FooterWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  flex-grow: 1;
  padding: 32px 8px;
`;

MedflytVisitLetsStartBottomSheet.ComingSoonPill = styled.div<{ showCompleted: boolean }>`
  padding: 2px 8px;
  border: 1px solid #cbd5e1;
  border-radius: 24px;
  font-weight: 600;
  font-size: 14px;
  color: ${(props) => (props.showCompleted ? "var(--tw-green-600)" : "var(--tw-slate-600)")};
`;

MedflytVisitLetsStartBottomSheet.Next = styled(AppIcon)`
  fill: var(--tw-slate-400);
  margin-right: 12px;
`;

MedflytVisitLetsStartBottomSheet.ClockInTimes = styled.div`
  display: flex;
  flex-direction: row;
  width: 50%;
  align-items: center;
  justify-content: center;
`;

MedflytVisitLetsStartBottomSheet.ClockOutTimes = styled.div`
  display: flex;
  flex-direction: row;
  width: 50%;
  align-items: center;
  justify-content: center;
`;

MedflytVisitLetsStartBottomSheet.SubmitReport = styled.div`
  bottom: 0;
  position: sticky;
  background: white;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding-top: 12px;
  padding-bottom: 12px;
  z-index: 15;
`;

MedflytVisitLetsStartBottomSheet.SubmitReportButton = styled(AppButton)`
  width: 80%;
`;

MedflytVisitLetsStartBottomSheet.ReportsHeader = styled(AppCardHeader)`
  position: sticky;
  top: -1px;
  background: white;
  z-index: 2;
  padding-top: 0;
  align-items: inherit;
`;

MedflytVisitLetsStartBottomSheet.PatientName = styled.span`
  font-size: 16px;
  padding-right: 4px;
  line-height: 16px;
`;

MedflytVisitLetsStartBottomSheet.BottomSheetWrapper = styled(Flex)`
  flex: 1;
`;

MedflytVisitLetsStartBottomSheet.ErrorModal = styled(AppAbstractModal)`
  z-index: 5;
  > .container {
    background: white;
    border-radius: 32px;
    padding: 24px;
    width: 80%;
    text-align: center;
  }
`;

MedflytVisitLetsStartBottomSheet.ErrorModalHeadline = styled.span`
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  font-size: 20px;
`;

MedflytVisitLetsStartBottomSheet.ErrorModalText = styled.span`
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  text-align: center;
`;

MedflytVisitLetsStartBottomSheet.Counter = styled.span`
  font-size: 14px;
  margin-left: 4px;
  color: var(--tw-slate-500);
`;

MedflytVisitLetsStartBottomSheet.Soft = styled.span`
  margin-left: 4px;
  color: var(--tw-slate-500);
`;

MedflytVisitLetsStartBottomSheet.VbpSubTitle = styled.span`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  margin: 12px 0 0 20px;
`;

MedflytVisitLetsStartBottomSheet.Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 0;
  box-sizing: border-box;
`;

MedflytVisitLetsStartBottomSheet.PatientDataWrapper = styled.div`
  display: inline-block;
`;

MedflytVisitLetsStartBottomSheet.ChangesSaved = styled.div`
  font-size: 14px;
  color: var(--blue);
  font-weight: 300;
  display: flex;
  align-items: center;
  gap: 4px;
`;

function DocumentsToCompleteCard(props: {
  taskDocumentsTotal: number;
  taskDocumentsCompleted: number;
}) {
  return (
    <MedflytVisitLetsStartBottomSheet.Card>
      <AppCardHeader>
        <Flex direction="row" xGap={12} alignItems="center" justifyContent="center">
          <DocumentsOutlineIcon height={20} width={20} color="var(--tw-slate-400)" />
          <Flex direction="row" alignItems="center" justifyContent="space-between">
            <MedflytVisitLetsStartBottomSheet.Soft>
              ({props.taskDocumentsCompleted}/{props.taskDocumentsTotal})
            </MedflytVisitLetsStartBottomSheet.Soft>
            <span>&nbsp;{translate(STRINGS.DOCUMENTS_TO_COMPLETE)}</span>
          </Flex>
        </Flex>
      </AppCardHeader>
    </MedflytVisitLetsStartBottomSheet.Card>
  );
}

function BloodPressureCard() {
  return (
    <MedflytVisitLetsStartBottomSheet.Card>
      <AppCardHeader>
        <Flex direction="row" xGap={12} alignItems="center" justifyContent="center">
          <BloodPressureVector height={20} width={20} />
          <Flex direction="row" alignItems="center" justifyContent="space-between">
            <span>{translate(STRINGS.BLOOD_PRESSURE)}</span>
          </Flex>
        </Flex>
        <MedflytVisitLetsStartBottomSheet.ComingSoonPill showCompleted={false}>
          {translate(STRINGS.COMING_SOON)}
        </MedflytVisitLetsStartBottomSheet.ComingSoonPill>
      </AppCardHeader>
    </MedflytVisitLetsStartBottomSheet.Card>
  );
}

function ReportCard(props: {
  vbpItemWithAnswers: NursingQuestionVBPWithAnswer[];
  onClickHeader: () => void;
}) {
  const requiredReportsLength = props.vbpItemWithAnswers.filter(
    (a) => a.answerDetails?.answer === ""
  ).length;
  const answeredReportsLength = props.vbpItemWithAnswers.filter(
    (a) => a.answerDetails?.answer !== ""
  ).length;

  return (
    <MedflytVisitLetsStartBottomSheet.Card hidden={props.vbpItemWithAnswers.length === 0}>
      <AppCardHeader onClick={props.onClickHeader}>
        <Flex direction="row" xGap={12} alignItems="center" justifyContent="center">
          <VbpVector height={20} width={20} />
          <Flex direction="row" alignItems="center" justifyContent="space-between">
            <span>{translate(STRINGS.PATIENT_REPORT)}</span>
            <MedflytVisitLetsStartBottomSheet.Counter>{`(${answeredReportsLength}/${props.vbpItemWithAnswers.length})`}</MedflytVisitLetsStartBottomSheet.Counter>
          </Flex>
        </Flex>

        {requiredReportsLength > 0 ? (
          <Flex direction="row" xGap={24} alignItems="center" justifyContent="flex-end">
            <MedflytVisitLetsStartBottomSheet.Next
              icon={CHEVRON_RIGHT_ICON}
              fill="var(--tw-slate-400)"
            />
          </Flex>
        ) : (
          <MedflytVisitLetsStartBottomSheet.ComingSoonPill showCompleted={true}>
            {translate(STRINGS.COMPLETED)}
          </MedflytVisitLetsStartBottomSheet.ComingSoonPill>
        )}
      </AppCardHeader>
    </MedflytVisitLetsStartBottomSheet.Card>
  );
}

function DutiesCard(props: {
  requiredDutyTasks: List<RequiredDutySheetTask>;
  isDutySheetCompleted: boolean;
  requiredTasksLength: number;
  requiredCompletedTasksLength: number;
  onClickHeader: () => void;
}) {
  return (
    <MedflytVisitLetsStartBottomSheet.Card>
      <AppCardHeader onClick={props.onClickHeader}>
        <Flex direction="row" xGap={12} alignItems="center" justifyContent="center">
          <DutiesVector height={20} width={20} />
          <Flex direction="row" alignItems="center" justifyContent="space-between">
            {props.requiredDutyTasks.length() > 0 ? (
              <>
                <span>{translate(STRINGS.DUTIES)}</span>
                <MedflytVisitLetsStartBottomSheet.Counter>{`(${props.requiredCompletedTasksLength}/${props.requiredTasksLength})`}</MedflytVisitLetsStartBottomSheet.Counter>
              </>
            ) : (
              <>
                <span>{translate(STRINGS.NO_DUTIES_REQUIRED_FOR_THIS_VISIT)}</span>
              </>
            )}
          </Flex>
        </Flex>

        {props.isDutySheetCompleted ? (
          <MedflytVisitLetsStartBottomSheet.ComingSoonPill showCompleted={true}>
            {translate(STRINGS.COMPLETED)}
          </MedflytVisitLetsStartBottomSheet.ComingSoonPill>
        ) : (
          <Flex direction="row" xGap={24} alignItems="center" justifyContent="flex-end">
            <If condition={props.requiredDutyTasks.length() > 0}>
              <MedflytVisitLetsStartBottomSheet.Next
                icon={CHEVRON_RIGHT_ICON}
                fill="var(--tw-slate-400)"
              />
            </If>
          </Flex>
        )}
      </AppCardHeader>
    </MedflytVisitLetsStartBottomSheet.Card>
  );
}

const getRequiredTasksLength = (requiredDutyTasks: List<RequiredDutySheetTask>) => {
  return requiredDutyTasks.filter((a) => a.isRequired).length();
};

const getRequiredCompletedTasksLength = (
  requiredDutyTasks: List<RequiredDutySheetTask>,
  mappedSelectedDuties: HashMap<PlanOfCareItemId, { notes: string | null }>
) => {
  return requiredDutyTasks
    .filter((a) => a.isRequired && mappedSelectedDuties.get(a.id) !== undefined)
    .length();
};

export default MedflytVisitLetsStartBottomSheet;
